import React, { useState, useContext } from "react";
import {
  Box,
  Container,
  Chip,
  Stack,
  Stepper,
  Step,
  Button,
  StepLabel,
} from "@mui/material";
import IndustryIcon from "@mui/icons-material/WarehouseRounded";
import ConceptIcon from "@mui/icons-material/TitleRounded";
import CaseTopics from "../CaseTopics";
import Company from "../Company";
import { useNavigate, useLocation } from "react-router-dom";
import PageHeader from "../../../../../components/PageHeader";
import * as casestudyService from "../../../services/CasestudyService";
import {
  BaseInfoLabel,
  createBaseInfoObject,
  validateBaseInfo,
  updateBaseInfoValues,
  handleBaseInfoInputChanges,
  BaseInfoPage,
} from "../baseInfo";
import {
  IndustryLabel,
  createIndustryObject,
  validateIndustry,
  updateIndustryValues,
  handleIndustryInputChanges,
  IndustryPage,
} from "../industry";
import {
  CompanyLabel,
  createCompanyObject,
  validateCompany,
  updateCompanyValues,
  handleCompanyInputChanges,
} from "../compny";
import {
  TopicsLabel,
  createTopicsObject,
  validateTopics,
  updateTopicsValues,
} from "../topic";
import { Completed } from "../Completed";
import Notification from "../../../../../components/mui/Notification";
import LoadingButton from "@mui/lab/LoadingButton";
import { AppContext } from "../../../App";
import { caseStudyCreated } from "../../../reducers/actions";
import { CASE_STUDY_TYPE_MINI } from "../../../helper/Constants";

export default function New() {
  const { state } = useLocation();
  const { dispatch } = useContext(AppContext);
  const initFormValues = () => {
    const baseInfo = state.content?.baseInfo
      ? state.content.baseInfo
      : createBaseInfoObject();
    const industry = state.content?.industry
      ? state.content.industry
      : createIndustryObject();
    const company = state.content?.company
      ? state.content.company
      : createCompanyObject();
    const topics = state.content?.topics
      ? state.content.topics
      : createTopicsObject();
    return { baseInfo, industry, company, topics };
  };

  const navigate = useNavigate();
  const [formValues, setFormValues] = useState(initFormValues);
  const [nextBtnLoading, setNextBtnLoading] = useState();
  const [hardRender, setHardRender] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [skipped, setSkipped] = useState(new Set());
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });
  const course = state.course;

  const steps = [
    {
      label: BaseInfoLabel,
      content: (
        <BaseInfoPage
          course={course}
          formValues={formValues}
          onInputChange={(e) => handleBaseInfoInputChanges(e, setFormValues)}
          updateValues={(name, value) =>
            updateBaseInfoValues(name, value, setFormValues)
          }
        />
      ),
      validationFn: validateBaseInfo,
      backgroundFn: [() => upsertBaseInfo()],
    },
    {
      label: IndustryLabel,
      content: (
        <IndustryPage
          course={course}
          formValues={formValues}
          onInputChange={(e) => handleIndustryInputChanges(e, setFormValues)}
          updateValues={(name, value) =>
            updateIndustryValues(name, value, setFormValues)
          }
        />
      ),
      validationFn: validateIndustry,
    },
    {
      label: CompanyLabel,
      content: (
        <Company
          formValues={formValues}
          onInputChange={(e) => handleCompanyInputChanges(e, setFormValues)}
          updateValues={(name, value) =>
            updateCompanyValues(name, value, setFormValues)
          }
        />
      ),
      validationFn: validateCompany,
      backgroundFn: [() => industryName()],
    },
    {
      label: TopicsLabel,
      content: (
        <CaseTopics
          course={course}
          formValues={formValues}
          updateValues={(name, value) =>
            updateTopicsValues(name, value, setFormValues)
          }
        />
      ),
      validationFn: validateTopics,
    },
  ];

  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  const handleSubmit = () => {
    const caseStudyRequest = {
      caseStudyId: formValues.caseStudyId,
      courseId: course.id,
      type: CASE_STUDY_TYPE_MINI,
      inputParams: {
        major: course.field,
        studentLevel: course.studentLevel,
        chapterTitle: formValues.baseInfo.chapters[0],
        companyTerritory: formValues.company.territory, // marketTerritory
        companyName: formValues.company.names[0],
        concept: formValues.concept,
        industryName: formValues.industry.name,
        industryCountry: formValues.industry.country,
        industryProvince: formValues.industry.province,
        topics: formValues.topics.list.join(", "),
        competitors: formValues.company.competitors.join(", "),
        chapterTopics: formValues.baseInfo.chapterTopics?.join(", "),
        locale: formValues.baseInfo.lang,
      },
    };

    casestudyService
      .create(caseStudyRequest)
      .then((res) => {
        dispatch(caseStudyCreated());
      })
      .catch((e) => {
        console.error(e);
        setNotify({
          isOpen: true,
          message: "Could not submit the case study. Please try again later",
          type: "error",
        });
      })
      .finally(() => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        // Scroll to the top of the page
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
  };

  const upsertBaseInfo = () => {
    setNextBtnLoading(true);
    const request = {
      id: formValues.caseStudyId,
      courseId: course.id,
      marketType: formValues.baseInfo.marketType,
      textBook: course.textBook,
      textbookChapterTitle: formValues.baseInfo.chapters[0],
      type: CASE_STUDY_TYPE_MINI,
    };

    casestudyService
      .upsertBaseInfo(request)
      .then((res) => {
        setFormValues((prevValues) => ({
          ...prevValues,
          caseStudyId: res.data,
        }));
        moveNext();
      })
      .catch((e) => {
        console.error(e);
        setNotify({
          isOpen: true,
          message:
            "Could not submit a case study draft. Please try again later",
          type: "error",
        });
      })
      .finally(() => setNextBtnLoading(false));
  };

  const industryName = () => {
    setNextBtnLoading(true);
    const params = {
      csId: formValues.caseStudyId,
      cName: formValues.company.names[0],
      lang: formValues.baseInfo.lang,
    };
    casestudyService
      .getIndustryName(params)
      .then((res) => {
        updateIndustryValues("name", res.data, setFormValues);
        moveNext();
      })
      .catch((e) => {
        console.error(e);
        setNotify({
          isOpen: true,
          message: "Could not get industry name. Please try again later",
          type: "error",
        });
      })
      .finally(() => setNextBtnLoading(false));
  };

  const moveNext = () => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
    // Scroll to the top of the page
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNext = () => {
    console.log("Mini handleNext: ", formValues);
    const currentStep = steps[activeStep];
    if (currentStep.validationFn(formValues)) {
      if (currentStep.backgroundFn) {
        currentStep.backgroundFn.forEach((func) => func());
      } else {
        moveNext();
      }
    } else {
      console.error("Not valid form:", formValues);
      setHardRender(!hardRender);
    }
  };

  const onCancel = () => {
    navigate(-1, { replace: true });
  };

  const handleReset = () => {
    setFormValues(initFormValues);
    setActiveStep(0);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <PageHeader title="New Mini Case Configuration" isChild={true} />
      <Box sx={{ width: "100%" }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((step, index) => {
            const stepProps = {};
            const labelProps = {};
            if (isStepSkipped(index)) {
              stepProps.completed = false;
            }
            return (
              <Step key={step.label} {...stepProps}>
                <StepLabel {...labelProps}>{step.label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
        {activeStep === steps.length ? (
          <Completed onReset={handleReset} />
        ) : (
          <React.Fragment>
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
              <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Stack direction="row" spacing={1}>
                  {formValues.concept ? (
                    <Chip icon={<ConceptIcon />} label={formValues.concept} />
                  ) : null}
                  {formValues.industry?.name ? (
                    <Chip
                      icon={<IndustryIcon />}
                      label={formValues.industry.name}
                    />
                  ) : null}
                </Stack>
              </Container>

              {steps[activeStep].content}
              <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                {activeStep === steps.length - 1 ? (
                  <Button onClick={handleSubmit}>Submit</Button>
                ) : (
                  <LoadingButton onClick={handleNext} loading={nextBtnLoading}>
                    Next
                  </LoadingButton>
                )}

                <Button color="secondary" onClick={onCancel} sx={{ mr: 1 }}>
                  Cancel
                </Button>
              </Box>
            </Container>
          </React.Fragment>
        )}
      </Box>
      <Notification notify={notify} setNotify={setNotify} />
    </Container>
  );
}
